// src/components/OptionFields/OptionFieldRenderer.tsx
// 拡張フィールド表示用コンポーネント

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Form, Input, Checkbox, Radio, Select, DatePicker, Typography, Button, Space } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import { OptionField } from '../../services/option-field-api';
import { OptionFieldValidator } from '../../utils/optionFieldValidation';
import OptionFieldChoiceModal from './OptionFieldChoiceModal';
import RangeFieldRenderer from './RangeFieldRenderer';
import CompareFieldRenderer from './CompareFieldRenderer';
import dayjs from 'dayjs';
import './OptionFieldRenderer.css';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface OptionFieldRendererProps {
  field: OptionField;
  value?: any;
  onChange?: (value: any) => void;
  prefix?: string;
  readonly?: boolean;
  // 範囲検索用
  rangeFromValue?: any;
  rangeToValue?: any;
  onRangeFromChange?: (value: any) => void;
  onRangeToChange?: (value: any) => void;
  isRangeSearch?: boolean;
  // 比較・統合モード用
  duplicateValue?: any;
  dupeSelectMode?: 'union' | 'dupe';
  onDupeSelectChange?: (mode: 'union' | 'dupe') => void;
  // モード管理
  kind?: 'detail' | 'search' | 'compare' | 'marge';
  // バリデーションエラー
  validationError?: string;
  /**
   * 選択肢モーダル（Type3 リストボックス）の zIndex
   * 親モーダルの zIndex より大きい値を指定すること
   * 未指定時は Ant Design デフォルト
   */
  modalZIndex?: number;
}

export interface OptionFieldRendererHandle {
  validateField: () => boolean;
  clearErrors: () => void;
}

/**
 * 拡張フィールドをレンダリングするコンポーネント
 */
export const OptionFieldRenderer = forwardRef<OptionFieldRendererHandle, OptionFieldRendererProps>(({
  field,
  value,
  onChange,
  prefix = '',
  readonly = false,
  rangeFromValue,
  rangeToValue,
  onRangeFromChange,
  onRangeToChange,
  isRangeSearch = false,
  duplicateValue,
  dupeSelectMode = 'union',
  onDupeSelectChange,
  kind = 'detail',
  validationError: externalValidationError,
  modalZIndex,
}, ref) => {
  const fieldName = `${prefix}option_${field.optionfieldkey}`;
  const fieldId = `${prefix}_option_${field.optionfieldkey}`;
  
  // Type 3 リストボックス用のモーダル状態管理
  const [choiceModalVisible, setChoiceModalVisible] = useState(false);
  
  // デフォルト値を取得
  const getDefaultValue = () => {
    // APIレスポンスの構造に合わせて複数のパスを確認
    return field.defaultValue || 
           field.optionconfiginfoMap?.default_value || 
           field.optionconfiginfoMap?.defaultValue ||
           null;
  };
  
  // オリジナル値（変更検知用）
  const [originalValue] = useState(value || getDefaultValue());
  
  // Input ref for DOM value correction
  const inputRef = useRef<any>(null);
  
  // リアルタイムバリデーション状態
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [showRequiredError, setShowRequiredError] = useState(false);
  
  // リアルタイムバリデーション関数
  const validateRealTime = (currentValue: any) => {
    let checkKindList: any = field.javascriptcheckKindList;
    
    // 文字列の場合はカンマ区切りで配列に変換
    if (typeof checkKindList === 'string') {
      checkKindList = checkKindList.split(',');
    }
    
    if (!checkKindList) {
      return;
    }
    
    // フィールドオブジェクトのコピーを作成して変換後の配列をセット
    const fieldCopy = { ...field, javascriptcheckKindList: checkKindList };
    const validationErrors = OptionFieldValidator.validateField(currentValue, fieldCopy);
    
    if (validationErrors.length > 0) {
      setValidationError(validationErrors[0].message);
    } else {
      setValidationError(null);
    }
  };

  // 必須フィールドのバリデーション関数
  const validateRequired = (currentValue: any) => {

    if (!isRequired()) {
      setShowRequiredError(false);
      return true;
    }

    let isEmpty = false;
    
    if (field.optionfieldtype === 2) {
      // 単一チェックボックスの場合
      isEmpty = !currentValue || currentValue === 'f';
    } else if (field.optionfieldtype === 4) {
      // 複数チェックボックスの場合
      isEmpty = !currentValue || (Array.isArray(currentValue) && currentValue.length === 0);
    } else {
      // その他のフィールドタイプ
      isEmpty = !currentValue || currentValue === '' || currentValue === null || currentValue === undefined;
    }

    setShowRequiredError(isEmpty);
    return !isEmpty;
  };

  // 全体のバリデーション関数
  const validateField = (currentValue: any) => {
    const isRequiredValid = validateRequired(currentValue);
    if (isRequiredValid) {
      validateRealTime(currentValue);
    } else {
      // 必須エラーがある場合は他のバリデーションエラーをクリア
      setValidationError(null);
    }
  };

  // 値の変換処理（半角変換、金額フォーマットなど）
  const convertValue = (inputValue: string, onBlur: boolean = false): string => {
    let convertedValue = inputValue;
    
    if (field.javascriptcheckKindList?.includes('acs_valid_convert_half') || 
        field.javascriptcheckKindList?.includes('acs_valid_tel')) {
      // 半角変換
      convertedValue = convertedValue.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
    }
    
    if (field.javascriptcheckKindList?.includes('acs_valid_price')) {
      if (onBlur) {
        // 金額3桁カンマ区切り
        const numValue = parseFloat(convertedValue.replace(/,/g, ''));
        if (!isNaN(numValue)) {
          convertedValue = numValue.toLocaleString();
        }
      } else {
        // フォーカス時はカンマを削除
        convertedValue = convertedValue.replace(/,/g, '');
      }
    }
    
    return convertedValue;
  };
  
  // 初期化時にデフォルト値が未設定の場合、強制的にデフォルト値を設定
  // DOM値の自動修正（必要な場合のみ）
  useEffect(() => {
    // 実際のDOMの値をチェック
    if (inputRef.current && field.optionfieldtype === 1) {
      const actualValue = inputRef.current.input?.value || inputRef.current.value;
      
      // DOM値が期待値と異なる場合は強制更新
      const expectedValue = (value !== undefined && value !== null && value !== '') ? String(value) : '';
      if (expectedValue && (actualValue !== expectedValue || actualValue === 'undefined' || actualValue === 'null')) {
        setTimeout(() => {
          if (inputRef.current) {
            try {
              if (inputRef.current.input) {
                inputRef.current.input.value = expectedValue;
                inputRef.current.input.dispatchEvent(new Event('input', { bubbles: true }));
              } else if (inputRef.current.value !== undefined) {
                inputRef.current.value = expectedValue;
                inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
              }
            } catch (error) {
              // Silent error handling
            }
          }
        }, 0);
      }
    }
  }, [value, field.optionfieldkey]);

  useEffect(() => {
    // 検索モードの場合はデフォルト値を適用しない
    if (kind === 'search') return;

    if ((value === undefined || value === null) && onChange) {
      const defaultVal = getDefaultValue();
      if (defaultVal !== undefined && defaultVal !== null && defaultVal !== '') {
        if (field.optionfieldtype === 2) {
          const booleanValue = defaultVal === 't' || defaultVal === 'true' || defaultVal === true;
          onChange(booleanValue ? 't' : 'f');
        } else {
          onChange(defaultVal);
        }
      }
    }
  }, [kind]);

  // 値が変更された時に必須バリデーションをチェック
  useEffect(() => {
    if (hasBlurred && isRequired()) {
      validateRequired(value);
    }
  }, [value, hasBlurred]);

  // コンポーネントマウント時に初期値チェック（テスト用）
  useEffect(() => {
    if (isRequired()) {
      // 必須フィールドは常にチェック（テスト用）
      validateRequired(value);
    }
  }, [field.optionfieldkey, value]);

  // 外部から呼び出し可能なメソッドを公開
  useImperativeHandle(ref, () => ({
    validateField: () => {
      const isValid = validateRequired(value);
      if (isValid) {
        validateRealTime(value);
      }
      return isValid && !validationError;
    },
    clearErrors: () => {
      setShowRequiredError(false);
      setValidationError(null);
    }
  }));
  
  // 値が変更されたかどうかを判定
  const isValueChanged = () => {
    if (field.optionfieldtype === 2) {
      // チェックボックスの場合は't'/'f'で比較
      const currentBool = value === true || value === 't' || value === '1';
      const originalBool = originalValue === true || originalValue === 't' || originalValue === '1';
      return currentBool !== originalBool;
    } else if (field.optionfieldtype === 4) {
      // 複数チェックボックスの場合は配列比較
      const currentArray = Array.isArray(value) ? value : (value ? [value] : []);
      const originalArray = Array.isArray(originalValue) ? originalValue : (originalValue ? [originalValue] : []);
      return JSON.stringify(currentArray.sort()) !== JSON.stringify(originalArray.sort());
    } else {
      // その他は文字列比較
      return String(value || '') !== String(originalValue || '');
    }
  };
  
  // CSSクラス名を動的に生成
  const getFieldClassName = () => {
    let className = getCssClass();
    
    if (isValueChanged()) {
      if (kind === 'compare' || kind === 'marge') {
        className += ' option-field-changed-c';
      } else if (kind === 'search') {
        className += ' option-field-search-changed';
      } else {
        className += ' option-field-changed';
      }
    }
    
    return className;
  };

  // 必須フィールドかどうか判定
  const isRequired = () => {
    let checkKindList: any = field.javascriptcheckKindList;
    
    // 文字列の場合はカンマ区切りで配列に変換
    if (typeof checkKindList === 'string') {
      checkKindList = checkKindList.split(',');
    }
    
    const hasRequireN = checkKindList?.includes('acs_valid_require_n');
    const hasRequireO = checkKindList?.includes('acs_valid_require_o');
    const result = hasRequireN || hasRequireO;
    
    
    return result;
  };

  // バリデーションルールを生成
  const getValidationRules = () => {
    // 検索モードの場合はバリデーションなし
    if (kind === 'search') {
      return [];
    }

    const rules: any[] = [];
    const checkKindList = field.javascriptcheckKindList || [];
    
    // 文字数制限
    if (field.javascriptcheckDigit && (field.optionfieldtype === 1 || checkKindList.includes('acs_valid_char_digit'))) {
      rules.push({ 
        max: field.javascriptcheckDigit, 
        message: `${field.dispname}は${field.javascriptcheckDigit}文字以内で入力してください` 
      });
    }

    // 数値バリデーション
    if (checkKindList.includes('acs_valid_numeric')) {
      rules.push({
        pattern: /^[0-9]*$/,
        message: `${field.dispname}は数値で入力してください`
      });
    }

    // 価格バリデーション（数値+カンマ区切り）
    if (checkKindList.includes('acs_valid_price')) {
      rules.push({
        pattern: /^[0-9,]*$/,
        message: `${field.dispname}は数値（カンマ区切り可）で入力してください`
      });
    }

    // メールアドレス
    if (checkKindList.includes('acs_valid_email')) {
      rules.push({
        type: 'email',
        message: `${field.dispname}は正しいメールアドレス形式で入力してください`
      });
    }

    // 半角文字
    if (checkKindList.includes('acs_valid_half')) {
      rules.push({
        pattern: /^[ -~]*$/,
        message: `${field.dispname}は半角文字で入力してください`
      });
    }

    // 電話番号
    if (checkKindList.includes('acs_valid_tel')) {
      rules.push({
        pattern: /^[0-9\+\-() ]*$/,
        message: `${field.dispname}は正しい電話番号形式で入力してください`
      });
    }

    // URL
    if (checkKindList.includes('acs_valid_url')) {
      rules.push({
        type: 'url',
        message: `${field.dispname}は正しいURL形式で入力してください`
      });
    }

    // 郵便番号
    if (checkKindList.includes('acs_valid_postcode')) {
      rules.push({
        pattern: /^[0-9-]*$/,
        message: `${field.dispname}は正しい郵便番号形式で入力してください`
      });
    }

    // 時刻
    if (checkKindList.includes('acs_valid_time')) {
      rules.push({
        pattern: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
        message: `${field.dispname}は正しい時刻形式（HH:MM）で入力してください`
      });
    }

    // 0以上の整数
    if (checkKindList.includes('acs_valid_pluszero')) {
      rules.push({
        validator: (_: any, value: any) => {
          if (!value) return Promise.resolve();
          const num = Number(value);
          if (isNaN(num) || !Number.isInteger(num) || num < 0) {
            return Promise.reject(new Error(`${field.dispname}は0以上の整数で入力してください`));
          }
          return Promise.resolve();
        }
      });
    }

    // 半角カタカナ
    if (checkKindList.includes('acs_valid_half_katakana')) {
      rules.push({
        pattern: /^[ｦ-ﾟ]*$/,
        message: `${field.dispname}は半角カタカナで入力してください`
      });
    }

    // ダブルクォート禁止
    if (checkKindList.includes('acs_valid_double_quote')) {
      rules.push({
        validator: (_: any, value: any) => {
          if (!value) return Promise.resolve();
          if (value.includes('"')) {
            return Promise.reject(new Error(`${field.dispname}にダブルクォート（"）は使用できません`));
          }
          return Promise.resolve();
        }
      });
    }

    // クォート・カンマ禁止
    if (checkKindList.includes('acs_valid_quotes_and_comma')) {
      rules.push({
        validator: (_: any, value: any) => {
          if (!value) return Promise.resolve();
          if (value.includes('"') || value.includes("'") || value.includes(',')) {
            return Promise.reject(new Error(`${field.dispname}にクォート（"'）やカンマ（,）は使用できません`));
          }
          return Promise.resolve();
        }
      });
    }

    // クォート禁止
    if (checkKindList.includes('acs_valid_quotes')) {
      rules.push({
        validator: (_: any, value: any) => {
          if (!value) return Promise.resolve();
          if (value.includes('"') || value.includes("'")) {
            return Promise.reject(new Error(`${field.dispname}にクォート（"'）は使用できません`));
          }
          return Promise.resolve();
        }
      });
    }

    // セレクトタグ選択必須
    if (checkKindList.includes('acs_valid_select_tag')) {
      rules.push({
        validator: (_: any, value: any) => {
          if (value === null || value === undefined || value === '' || value === -1) {
            return Promise.reject(new Error(`${field.dispname}を選択してください`));
          }
          return Promise.resolve();
        }
      });
    }

    // 複数メールアドレス
    if (checkKindList.includes('acs_valid_multiple_email')) {
      rules.push({
        validator: (_: any, value: any) => {
          if (!value) return Promise.resolve();
          const emails = value.split(',').map((email: string) => email.trim());
          const emailRegex = /^[a-zA-Z0-9_\.\-]+@[a-zA-Z0-9\-]+\.[a-zA-Z0-9\.\-]+$/;
          
          for (const email of emails) {
            if (email && !emailRegex.test(email)) {
              return Promise.reject(new Error(`${field.dispname}に無効なメールアドレスが含まれています: ${email}`));
            }
          }
          return Promise.resolve();
        }
      });
    }

    // 小数点桁数制限
    if (checkKindList.includes('acs_valid_decimal_point')) {
      const digit = field.javascriptcheckDigit;
      rules.push({
        validator: (_: any, value: any) => {
          if (!value) return Promise.resolve();
          const num = Number(value);
          if (isNaN(num)) return Promise.resolve(); // 数値チェックは別途実施
          
          if (!Number.isInteger(num)) {
            const decimalPart = value.toString().split('.')[1];
            if (decimalPart && digit && decimalPart.length > digit) {
              return Promise.reject(new Error(`${field.dispname}の小数点以下は${digit}桁以内で入力してください`));
            }
          }
          return Promise.resolve();
        }
      });
    }

    // 数値範囲チェック
    if (checkKindList.includes('acs_valid_numeric_range')) {
      const digitVal = field.javascriptcheckDigit;
      if (digitVal && typeof digitVal === 'string' && (digitVal as string).includes('-')) {
        const range = (digitVal as string).split('-').map((val: string) => Number(val));
        rules.push({
          validator: (_: any, value: any) => {
            if (!value) return Promise.resolve();
            const num = Number(value);
            if (isNaN(num)) return Promise.resolve();
            
            if (num < range[0] || num > range[1]) {
              return Promise.reject(new Error(`${field.dispname}は${range[0]}以上${range[1]}以下で入力してください`));
            }
            return Promise.resolve();
          }
        });
      }
    }

    // 価格範囲チェック
    if (checkKindList.includes('acs_valid_price_range')) {
      const digitVal = field.javascriptcheckDigit;
      if (digitVal && typeof digitVal === 'string' && (digitVal as string).includes('-')) {
        const range = (digitVal as string).split('-').map((val: string) => Number(val));
        rules.push({
          validator: (_: any, value: any) => {
            if (!value) return Promise.resolve();
            const cleanValue = value.replace(/,/g, '');
            const num = Number(cleanValue);
            if (isNaN(num)) return Promise.resolve();
            
            if (num < range[0] || num > range[1]) {
              return Promise.reject(new Error(`${field.dispname}は${range[0].toLocaleString()}以上${range[1].toLocaleString()}以下で入力してください`));
            }
            return Promise.resolve();
          }
        });
      }
    }

    // 禁則文字チェック
    if (checkKindList.includes('acs_valid_prohibited_characters')) {
      // 禁則文字は別途設定が必要なため、基本的な危険文字をチェック
      rules.push({
        validator: (_: any, value: any) => {
          if (!value) return Promise.resolve();
          const prohibitedChars = ['<', '>', '&', 'script', 'javascript:'];
          
          for (const char of prohibitedChars) {
            if (value.toLowerCase().includes(char)) {
              return Promise.reject(new Error(`${field.dispname}に使用できない文字が含まれています`));
            }
          }
          return Promise.resolve();
        }
      });
    }

    // HTMLエディタ必須
    if (checkKindList.includes('acs_valid_require_htmledit')) {
      rules.push({
        validator: (_: any, value: any) => {
          if (!value || value.trim() === '' || value.trim() === '<p></p>') {
            return Promise.reject(new Error(`${field.dispname}は必須入力です`));
          }
          return Promise.resolve();
        }
      });
    }

    // 電話番号半角チェック
    if (checkKindList.includes('acs_valid_tel_han')) {
      rules.push({
        pattern: /^[0-9\+\-() ]*$/,
        message: `${field.dispname}は半角文字で入力してください`
      });
    }
    
    return rules;
  };

  // CSS クラス名を取得
  const getCssClass = () => {
    let className = field.cssClass || '';
    if (field.optionconfiginfoMap?.cssClass) {
      className += ` ${field.optionconfiginfoMap.cssClass}`;
    }
    return className;
  };

  // 半角変換処理
  const convertToHalfWidth = (value: string) => {
    return value.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => 
      String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
    ).replace(/　/g, ' ');
  };

  // カンマ区切り追加（金額用）
  const addCommaToPrice = (value: string) => {
    if (!value) return value;
    const num = value.replace(/,/g, '');
    if (!/^\d+$/.test(num)) return value;
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // カンマ除去
  const removeCommaFromPrice = (value: string) => {
    return value ? value.replace(/,/g, '') : value;
  };

  // テキストフィールドのイベントハンドラー
  const handleTextChange = (value: string) => {
    let processedValue = value;
    const checkKindList = field.javascriptcheckKindList || [];

    // 半角変換
    if (checkKindList.includes('acs_valid_convert_half') || checkKindList.includes('acs_valid_tel')) {
      processedValue = convertToHalfWidth(processedValue);
    }

    onChange?.(processedValue);
  };

  // テキストフィールドのフォーカスアウト処理
  const handleTextBlur = (value: string) => {
    setHasBlurred(true);
    const checkKindList = field.javascriptcheckKindList || [];
    let processedValue = value;

    // 半角変換
    if (checkKindList.includes('acs_valid_convert_half') || checkKindList.includes('acs_valid_tel')) {
      processedValue = convertToHalfWidth(processedValue);
    }

    // 金額フォーマット（カンマ区切り追加）
    if (checkKindList.includes('acs_valid_price')) {
      processedValue = addCommaToPrice(processedValue);
    }

    // 全体のバリデーション（フォーカスアウト時に実行）
    validateField(processedValue);

    if (processedValue !== value) {
      onChange?.(processedValue);
    }
  };

  // テキストフィールドのフォーカス処理
  const handleTextFocus = (value: string) => {
    const checkKindList = field.javascriptcheckKindList || [];
    let processedValue = value;

    // 金額フォーマット（カンマ除去）
    if (checkKindList.includes('acs_valid_price')) {
      processedValue = removeCommaFromPrice(processedValue);
    }

    if (processedValue !== value) {
      onChange?.(processedValue);
    }
  };

  // 読み取り専用表示用のコンポーネント
  const renderReadOnlyValue = () => {
    if (!value && value !== 0 && value !== false) {
      return <Text type="secondary">-</Text>;
    }

    switch (field.optionfieldtype) {
      case 1: // テキスト入力
        const rowCount = field.row || field.optionconfiginfoMap?.row || 1;
        const isMultiLine = rowCount > 1;
        const prefixText = field.prefix || field.optionconfiginfoMap?.prefix;
        const suffixText = field.suffix || field.optionconfiginfoMap?.suffix;
        
        const displayText = prefixText || suffixText ? 
          `${prefixText || ''}${value}${suffixText || ''}` : value;
        
        return isMultiLine ? (
          <div style={{ whiteSpace: 'pre-wrap', padding: '8px', backgroundColor: '#fafafa', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
            {displayText}
          </div>
        ) : (
          <Text>{displayText}</Text>
        );
      
      case 2: // 単一チェックボックス
        const isChecked = value === true || value === 't' || value === '1' || value === 'true';
        return <Text>{isChecked ? 'チェック済み' : 'チェックなし'}</Text>;
      
      case 3: // リストボックス
        return <Text>{value}</Text>;
      
      case 4: // 複数チェックボックス
        const checkedValues = Array.isArray(value) ? value : (value ? [value] : []);
        return checkedValues.length > 0 ? (
          <div>
            {checkedValues.map((val, index) => (
              <div key={index} style={{ marginBottom: '4px' }}>
                <Text>• {val}</Text>
              </div>
            ))}
          </div>
        ) : (
          <Text type="secondary">-</Text>
        );
      
      case 5: // セレクトボックス
        return <Text>{value || '未選択'}</Text>;
      
      case 6: // 日付選択
        return <Text>{value || '未設定'}</Text>;
      
      default:
        return <Text>{String(value)}</Text>;
    }
  };

  // フィールドタイプに応じた表示コンポーネントを返す
  const renderField = () => {
    // 読み取り専用モードの場合
    if (readonly) {
      return renderReadOnlyValue();
    }

    // 比較・統合モードの場合は専用コンポーネントを使用
    if (kind === 'compare' || kind === 'marge') {
      return (
        <CompareFieldRenderer
          currentValue={value}
          duplicateValue={duplicateValue}
          onCurrentChange={onChange || (() => {})}
          onDupeSelectChange={onDupeSelectChange || (() => {})}
          dupeSelectMode={dupeSelectMode}
          fieldName={fieldName}
          placeholder={field.dispname}
          className={getFieldClassName()}
          isReadOnly={kind === 'marge'}
        />
      );
    }

    switch (field.optionfieldtype) {
      case 1: // テキスト入力
        const rowCount = field.row || field.optionconfiginfoMap?.row || 1;
        const isMultiLine = rowCount > 1;
        const defaultTextValue = kind === 'detail' ? getDefaultValue() : undefined;
        // 値が設定されている場合はそれを使用、そうでなければデフォルト値を使用
        // React controlled inputのため、nullやundefinedではなく空文字列にフォールバック
        // 明示的に空文字列の場合も、デフォルト値があれば使用する
        // React controlled inputでundefinedを避けるために厳密にチェック
        let textValue = '';
        if (value !== undefined && value !== null ) {
          textValue = String(value);
        } else if (defaultTextValue !== undefined && defaultTextValue !== null && defaultTextValue !== '') {
          textValue = String(defaultTextValue);
        }
        
        // さらに安全対策として、undefinedやnullが文字列化されていないか確認
        if (textValue === 'undefined' || textValue === 'null') {
          textValue = defaultTextValue || '';
        }
        
        
        const prefixText = field.prefix || field.optionconfiginfoMap?.prefix;
        const suffixText = field.suffix || field.optionconfiginfoMap?.suffix;
        
        // 範囲検索の場合
        if (isRangeSearch && field.optionconfiginfoMap?.searchRangeSelect) {
          return (
            <RangeFieldRenderer
              fieldType={1}
              fromValue={rangeFromValue}
              toValue={rangeToValue}
              displayValue={textValue}
              onFromChange={onRangeFromChange || (() => {})}
              onToChange={onRangeToChange || (() => {})}
              onDisplayChange={(val) => onChange?.(val)}
              placeholder={field.dispname}
              className={getFieldClassName()}
            />
          );
        }
        
        const inputElement = isMultiLine ? (
          <TextArea
            ref={inputRef}
            key={`textarea-${field.optionfieldkey}`}
            rows={rowCount}
            value={textValue || ''}
            defaultValue={textValue || ''}
            onChange={(e) => handleTextChange(e.target.value)}
            onBlur={(e) => handleTextBlur(e.target.value)}
            onFocus={(e) => handleTextFocus(e.target.value)}
            className={getFieldClassName()}
            placeholder={field.dispname}
            status={kind !== 'search' && (showRequiredError || validationError) ? 'error' : undefined}
          />
        ) : (
          <Input
            ref={inputRef}
            key={`input-${field.optionfieldkey}`}
            value={textValue || ''}
            defaultValue={textValue || ''}
            onChange={(e) => handleTextChange(e.target.value)}
            onBlur={(e) => handleTextBlur(e.target.value)}
            onFocus={(e) => handleTextFocus(e.target.value)}
            className={getFieldClassName()}
            placeholder={field.dispname}
            addonBefore={kind !== 'search' ? (prefixText || undefined) : undefined}
            addonAfter={kind !== 'search' ? (suffixText || undefined) : undefined}
            status={kind !== 'search' && (showRequiredError || validationError) ? 'error' : undefined}
          />
        );
        
        return (
          <div>
            {inputElement}
            {kind !== 'search' && (showRequiredError || validationError || externalValidationError) && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                {showRequiredError ? `${field.dispname}は必須入力です` : (externalValidationError || validationError)}
              </div>
            )}
          </div>
        );

      case 2: // 単一チェックボックス
        if (kind === 'search') {
          const selectValue = 
            value === true || value === 't' ? 't' :
            value === false || value === 'f' ? 'f' :
            undefined;

          return (
            <Select
              value={selectValue}
              onChange={(val) => {
                if (val === 't') {
                  onChange?.('t');
                } else if (val === 'f') {
                  onChange?.('f');
                } else {
                  onChange?.(undefined);
                }
              }}
              placeholder={field.dispname}
              style={{ width: '100%' }}
            >
              <Select.Option value={undefined}>
                <span style={{ color: '#bfbfbf' }}>{field.dispname}</span>
              </Select.Option>
              <Select.Option value="t">On</Select.Option>
              <Select.Option value="f">Off</Select.Option>
            </Select>
          );
        }

        const defaultChecked = kind === 'detail' 
          ? (getDefaultValue() === 't' || getDefaultValue() === 'true' || getDefaultValue() === true)
          : false;
        // 値が設定されている場合はそれを使用、そうでなければデフォルト値を使用
        const isChecked = value !== undefined && value !== null ? 
          (value === true || value === 't' || value === '1' || value === 'true') : 
          defaultChecked;

        const handleCheckboxChange = (e: any) => {
          const newValue = e.target.checked ? 't' : 'f';
          validateField(newValue);
          onChange?.(newValue);
        };

        return (
          <div>
            <Checkbox
              key={`checkbox-${field.optionfieldkey}`}
              checked={isChecked}
              onChange={handleCheckboxChange}
              className={getFieldClassName()}
            />
            {(showRequiredError || validationError || externalValidationError) && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                {showRequiredError ? `${field.dispname}は必須入力です` : (externalValidationError || validationError)}
              </div>
            )}
          </div>
        );

      case 3: // リストボックス（選択肢から選択）
        if (!field.optioninfo || !Array.isArray(field.optioninfo)) return null;
        
        const defaultListValue = kind === 'detail' ? getDefaultValue() : undefined;
        const listValue = value !== undefined && value !== null ? value : defaultListValue;
        
        const handleListChange = (newValue: string) => {
          validateField(newValue);
          onChange?.(newValue);
        };
        
        const handleListBlur = (e: React.FocusEvent<HTMLInputElement>) => {
          setHasBlurred(true);
          validateField(e.target.value);
        };
        
        return (
          <div>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={listValue || ''}
                onChange={(e) => handleListChange(e.target.value)}
                onBlur={handleListBlur}
                className={getFieldClassName()}
                placeholder={field.dispname}
                style={{ flexGrow: 1 }}
                readOnly={!field.optionconfiginfoMap?.direct_input}
                status={kind !== 'search' && (showRequiredError || validationError) ? 'error' : undefined}
              />
              <Button
                onClick={() => setChoiceModalVisible(true)}
                style={{ flexShrink: 0 }}
              >
                選択
              </Button>
              <Button
                onClick={() => onChange?.('')}
                icon={<CloseCircleFilled style={{ color: '#bfbfbf' }} />}
                style={{ flexShrink: 0, padding: '0 4px', border: '1px solid #e0e0e0', boxShadow: 'none', background: 'transparent' }}
                title="クリア"
              />
            </Space.Compact>
            
            {kind !== 'search' && (showRequiredError || validationError || externalValidationError) && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                {showRequiredError ? `${field.dispname}は必須入力です` : (externalValidationError || validationError)}
              </div>
            )}
            
            <OptionFieldChoiceModal
              visible={choiceModalVisible}
              title={field.dispname}
              options={field.optioninfo}
              zIndex={modalZIndex}
              onSelect={(selectedValue) => {
                handleListChange(selectedValue);
                setChoiceModalVisible(false);
              }}
              onCancel={() => setChoiceModalVisible(false)}
            />
          </div>
        );

      case 4: // 複数チェックボックス
        if (!field.optioninfo || !Array.isArray(field.optioninfo)) return null;
        
        const defaultMultiValue = kind === 'detail' ? getDefaultValue() : undefined;
        const defaultMultiArray = defaultMultiValue ? (Array.isArray(defaultMultiValue) ? defaultMultiValue : [defaultMultiValue]) : [];
        const checkedValues = value !== undefined && value !== null ? (Array.isArray(value) ? value : (value ? [value] : [])) : defaultMultiArray;
        
        const handleIndividualCheckboxChange = (option: string, checked: boolean) => {
          let newValues = [...checkedValues];
          if (checked) {
            if (!newValues.includes(option)) {
              newValues.push(option);
            }
          } else {
            newValues = newValues.filter(val => val !== option);
          }
          validateField(newValues);
          onChange?.(newValues);
        };

        return (
          <div>
            {field.optioninfo.map((option, index) => (
              <div key={index} style={{ marginBottom: '8px' }}>
                <Checkbox
                  checked={checkedValues.includes(option)}
                  onChange={(e) => handleIndividualCheckboxChange(option, e.target.checked)}
                  className={getFieldClassName()}
                >
                  {option}
                </Checkbox>
              </div>
            ))}
            {kind !== 'search' && (showRequiredError || validationError || externalValidationError) && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                {showRequiredError ? `${field.dispname}は必須入力です` : (externalValidationError || validationError)}
              </div>
            )}
          </div>
        );

      case 5: // セレクトボックス
        if (!field.optioninfo || !Array.isArray(field.optioninfo)) return null;
        
        const defaultSelectValue = kind === 'detail' ? getDefaultValue() : undefined;
        const selectValue = value !== undefined && value !== null ? value : defaultSelectValue;
        
        const handleSelectChange = (selectedValue: any) => {
          setHasBlurred(true);
          validateField(selectedValue);
          onChange?.(selectedValue);
        };
        
        return (
          <div>
            <Select
              value={selectValue}
              onChange={handleSelectChange}
              className={getFieldClassName()}
              placeholder={`${field.dispname}を選択してください`}
              style={{ width: '100%' }}
              status={kind !== 'search' && (showRequiredError || validationError) ? 'error' : undefined}
            >
              {field.optioninfo.map((option, index) => (
                <Option key={index} value={option}>
                  {option === '' ? '-- 未選択 --' : option}
                </Option>
              ))}
            </Select>
            {kind !== 'search' && (showRequiredError || validationError || externalValidationError) && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                {showRequiredError ? `${field.dispname}は必須入力です` : (externalValidationError || validationError)}
              </div>
            )}
          </div>
        );

      case 6: // 日付選択
        const dateFormat = field.format || field.optionconfiginfoMap?.format || 'YYYY/MM/DD';
        const picker = dateFormat.includes('MM') && !dateFormat.includes('DD') ? 'month' : 'date';
        const defaultDateValue = kind === 'detail' ? getDefaultValue() : undefined;
        const dateValue = value !== undefined && value !== null ? value : defaultDateValue;
        
        // 範囲検索の場合
        if (kind === 'search') {
          return (
            <RangeFieldRenderer
              fieldType={6}
              format={dateFormat}
              fromValue={rangeFromValue}
              toValue={rangeToValue}
              displayValue={dateValue}
              onFromChange={onRangeFromChange || (() => {})}
              onToChange={onRangeToChange || (() => {})}
              onDisplayChange={(val) => onChange?.(val)}
              placeholder={field.dispname}
              className={getFieldClassName()}
            />
          );
        }
        
        const handleDateChange = (date: any) => {
          const newValue = date ? date.format(dateFormat) : '';
          setHasBlurred(true);
          validateField(newValue);
          onChange?.(newValue);
        };

        const handleDatePickerOpen = () => {
          // カレンダーが開いたときに画面サイズに応じてスケーリングと位置調整
          const adjustCalendarPosition = () => {
            const dropdown = document.querySelector('.ant-picker-dropdown') as HTMLElement;
            if (dropdown) {
              const screenWidth = window.innerWidth;
              const screenHeight = window.innerHeight;
              const visualViewportHeight = window.visualViewport ? window.visualViewport.height : screenHeight;
              
              // キーボードが表示されているかを判定
              const isKeyboardVisible = visualViewportHeight < screenHeight * 0.8;
              
              // モーダル全体を適切な位置に配置
              dropdown.style.position = 'fixed';
              dropdown.style.left = '50%';
              dropdown.style.zIndex = '9999';
              dropdown.style.overflow = 'hidden';
              
              if (isKeyboardVisible) {
                // キーボード表示時は画面上部に配置
                dropdown.style.top = '10px';
                dropdown.style.transform = 'translateX(-50%)';
                dropdown.style.maxWidth = '95vw';
                dropdown.style.maxHeight = `${Math.min(visualViewportHeight - 20, 400)}px`;
              } else {
                // 通常時は画面中央に配置
                dropdown.style.top = '50%';
                dropdown.style.transform = 'translate(-50%, -50%)';
                dropdown.style.maxWidth = '95vw';
                dropdown.style.maxHeight = '95vh';
              }
              
              const panelContainer = dropdown.querySelector('.ant-picker-panel-container') as HTMLElement;
              if (panelContainer) {
                panelContainer.style.width = '100%';
                panelContainer.style.height = '100%';
                panelContainer.style.display = 'flex';
                panelContainer.style.justifyContent = 'center';
                panelContainer.style.alignItems = 'center';
                panelContainer.style.overflow = 'hidden';
              }
              
              const panel = dropdown.querySelector('.ant-picker-panel') as HTMLElement;
              if (panel) {
                // パネルを親コンテナ内に収める
                panel.style.maxWidth = 'calc(100% - 20px)';
                panel.style.maxHeight = 'calc(100% - 20px)';
                panel.style.margin = '10px';
                panel.style.boxSizing = 'border-box';
                
                // 必要に応じてスケーリング
                const panelRect = panel.getBoundingClientRect();
                const containerRect = dropdown.getBoundingClientRect();
                
                let scale = 1;
                
                // 幅に基づくスケーリング
                if (panelRect.width > containerRect.width - 40) {
                  scale = Math.min(scale, (containerRect.width - 40) / panelRect.width);
                }
                
                // 高さに基づくスケーリング  
                if (panelRect.height > containerRect.height - 40) {
                  scale = Math.min(scale, (containerRect.height - 40) / panelRect.height);
                }
                
                if (scale < 1) {
                  panel.style.transform = `scale(${scale})`;
                  panel.style.transformOrigin = 'center center';
                }
              }
            }
          };
          
          setTimeout(adjustCalendarPosition, 50);
          
          // Visual Viewport APIが利用可能な場合、キーボード表示/非表示を監視
          if (window.visualViewport) {
            const handleViewportChange = () => {
              setTimeout(adjustCalendarPosition, 100);
            };
            window.visualViewport.addEventListener('resize', handleViewportChange);
            
            // クリーンアップのため、モーダルが閉じられたときにリスナーを削除
            setTimeout(() => {
              if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleViewportChange);
              }
            }, 10000); // 10秒後にクリーンアップ
          }
        };
        
        return (
          <div>
            <Space.Compact style={{ width: '100%' }}>
              <DatePicker
                value={dateValue ? dayjs(dateValue) : null}
                onChange={handleDateChange}
                onOpenChange={(open) => {
                  if (open) {
                    handleDatePickerOpen();
                  }
                }}
                className={getFieldClassName()}
                format={dateFormat}
                picker={picker}
                style={{ flexGrow: 1 }}
                placeholder={field.dispname}
                showToday={false}
                status={(showRequiredError || validationError) ? 'error' : undefined}
                getPopupContainer={() => document.body}
                placement="bottomLeft"
              />
              <Button
                onClick={() => {
                  handleDateChange('');
                }}
                icon={<CloseCircleFilled style={{ color: '#bfbfbf' }} />}
                style={{ flexShrink: 0, padding: '0 4px', border: '1px solid #e0e0e0', boxShadow: 'none', background: 'transparent' }}
                title="クリア"
              />
            </Space.Compact>
            {(showRequiredError || validationError || externalValidationError) && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                {showRequiredError ? `${field.dispname}は必須入力です` : (externalValidationError || validationError)}
              </div>
            )}
          </div>
        );

      default:
        return <Input disabled placeholder={`未対応のフィールドタイプ: ${field.optionfieldtype}`} />;
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong>
          {field.dispname}
          {kind !== 'search' && !readonly && isRequired() && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
        </Text>
      </div>
      {readonly ? (
        renderField()
      ) : (
        <Form.Item 
          name={fieldName}
          rules={getValidationRules()}
          style={{ marginBottom: 0 }}
        >
          {renderField()}
        </Form.Item>
      )}
    </div>
  );
});

OptionFieldRenderer.displayName = 'OptionFieldRenderer';

export default OptionFieldRenderer;